/**
 * A profile video as the admin API serves it (see the API's
 * normalizeProfileVideosForResponse).
 *
 * Jowa profiles lead with a video, so every admin surface that renders a user
 * renders one of these. The R2 keys never leave the API — each URL here points
 * at the public `/public/profile-videos/*` proxy and needs no auth header,
 * which is what lets a plain `<video>` element play it.
 */
export type ProfileVideoStatus =
  | 'uploading'
  | 'processing'
  | 'processing_failed'
  | 'moderating'
  | 'ready'
  | 'rejected'
  | 'aborted'
  | 'failed';

export interface ProfileVideoRendition {
  url: string;
  width: number | null;
  height: number | null;
  bitrate_bps: number | null;
  size_bytes: number | null;
}

export interface ProfileVideo {
  video_id: number;
  public_id: string;
  /** Slot on the profile, 1-based. Slot 1 is the face of the profile. */
  place: number;
  caption: string | null;
  status: ProfileVideoStatus;
  /** Why a video was rejected or failed, when the pipeline recorded one. */
  status_reason: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  has_audio: boolean;
  is_primary: boolean;
  original_filename: string | null;
  original_size_bytes: number | null;
  /** Still frame. Null until the transcoder has run. */
  poster_url: string | null;
  thumb_url: string | null;
  /** Adaptive stream. Safari plays it natively; other browsers need the MP4. */
  hls_url: string | null;
  /** Progressive MP4 — what the admin player uses, since every browser plays it. */
  mp4_url: string | null;
  blur_poster_url: string | null;
  renditions: { [name: string]: ProfileVideoRendition } | null;
  moderation_labels: any | null;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/** Only a ready video has bytes to play; every other status is a state to explain. */
export const isPlayableVideo = (video: ProfileVideo | null | undefined): boolean =>
  Boolean(video && video.status === 'ready' && (video.mp4_url || video.hls_url));

/** "0:08", "1:04" — the badge on a tile, so length is visible before playing. */
export const formatVideoDuration = (durationMs: number | null | undefined): string => {
  const ms = Number(durationMs);
  if (!Number.isFinite(ms) || ms <= 0) {
    return '';
  }
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${`${seconds}`.padStart(2, '0')}`;
};

/**
 * Human label per status, for the badge on a tile that cannot be played.
 * An admin opening this page is usually looking for exactly these.
 */
export const VIDEO_STATUS_LABELS: { [key in ProfileVideoStatus]: string } = {
  uploading: 'Uploading',
  processing: 'Processing',
  processing_failed: 'Transcode failed',
  moderating: 'In moderation',
  ready: 'Ready',
  rejected: 'Rejected',
  aborted: 'Aborted',
  failed: 'Failed',
};
